import { useEffect, useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Pagination, QueryErrorAlert, Table, TextLink } from '@shared/components';
import { usePermissions } from '@/features/auth';
import { useRoleUsersQuery } from '../hooks/useRoleUsersQuery';
import type { RoleUser, RoleUsersParams } from '../types/roles.types';

interface Props {
  roleId: string;
}

// The shared Pagination offers 100 per page; this endpoint 422s above 50,
// so clamp here rather than narrowing the shared component.
const MAX_PER_PAGE = 50;

export function RoleUsersPanel({ roleId }: Props) {
  const [pagination, setPagination] = useState<RoleUsersParams>({ page: 1, per_page: 20 });
  const query = useRoleUsersQuery(roleId, pagination);
  const { hasPermission } = usePermissions();
  const canReadUsers = hasPermission({ resource: 'user', action: 'read' });

  const users = query.data?.data ?? [];
  const meta = query.data?.meta;
  const lastPage = meta?.last_page;
  const pageBound = lastPage !== undefined && lastPage >= 1 ? lastPage : 1;
  // Keep-previous-data holds out-of-range meta while the corrected page loads, so the
  // pager renders and clamps against this bound rather than the stale current_page.
  const effectivePage = Math.min(pagination.page, pageBound);

  // Assignments can shrink under a viewer sitting on a later page, which returns an
  // empty page; fall back to the last page the server still has.
  useEffect(() => {
    if (lastPage === undefined || lastPage < 1) return;
    setPagination((prev) => (prev.page > lastPage ? { ...prev, page: lastPage } : prev));
  }, [lastPage]);

  const handlePageChange = (page: number) => {
    const next = Math.min(Math.max(page, 1), pageBound);
    setPagination((prev) => (prev.page === next ? prev : { ...prev, page: next }));
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination({ page: 1, per_page: Math.min(perPage, MAX_PER_PAGE) });
  };

  const columns = useMemo<ColumnDef<RoleUser>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: (info) => {
          const user = info.row.original;
          const label = user.name ?? <span className="font-mono text-xs">{user.id}</span>;
          return canReadUsers ? (
            <TextLink to={`/admin/users/${user.id}`} color="secondary">
              {label}
            </TextLink>
          ) : (
            <span className="text-sm">{label}</span>
          );
        },
      },
      {
        id: 'email',
        header: 'Email',
        cell: (info) => <span className="text-sm">{info.row.original.email ?? '—'}</span>,
      },
    ],
    [canReadUsers],
  );

  const isEmptyRole = users.length === 0 && (!meta || meta.total === 0);

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">Assigned users{meta ? ` (${meta.total})` : ''}</h3>

      {query.isError ? (
        <QueryErrorAlert message="Could not load assigned users." onRetry={() => query.refetch()} isRetrying={query.isFetching} />
      ) : query.isLoading ? (
        <LoadingSpinner size="md" className="py-8" message="Loading assigned users..." />
      ) : isEmptyRole ? (
        <p className="text-mp-muted text-sm">No users are assigned to this role.</p>
      ) : (
        <>
          <Table<RoleUser> data={users} columns={columns} enableSorting={false} />
          {meta && meta.last_page > 1 && (
            <Pagination
              currentPage={effectivePage}
              lastPage={pageBound}
              total={meta.total}
              perPage={meta.per_page}
              from={meta.from}
              to={meta.to}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          )}
        </>
      )}
    </div>
  );
}
